import { hitTestStroke, type Stroke } from '@awwyeah/shared';
import type * as Y from 'yjs';

/**
 * Mutation helpers for a `sketch:<id>` doc's `Y.Array<Stroke>` ("strokes"). Deliberately
 * stroke-granularity, not point-granularity — see the module's `SketchPadPage.tsx` header
 * comment for why streaming partial strokes through Yjs was rejected in favor of drawing
 * locally while in progress and pushing one finished stroke on pointerup.
 *
 * A whole stroke is one array element (unlike list items, which are id-keyed `Y.Map`
 * entries) because a stroke is never edited in place after it's drawn — it is only ever
 * appended or removed wholesale, so the "replace-in-place forces a delete+insert" hazard
 * that makes item-as-array-element wrong for lists (docs/DECISIONS.md, WS-8) doesn't apply
 * here: two members drawing concurrently just append two strokes, and one member's delete
 * removes one array entry without touching anyone else's.
 */

export function appendStroke(array: Y.Array<Stroke>, stroke: Stroke): void {
  array.push([stroke]);
}

/** Removes one stroke by id, wherever it sits in the array. No-op if it's already gone
 * (a concurrent delete from another client, or the eraser tapping twice). */
export function deleteStrokeById(array: Y.Array<Stroke>, strokeId: string): boolean {
  const items = array.toArray();
  const index = items.findIndex((s) => s.id === strokeId);
  if (index === -1) return false;
  array.delete(index, 1);
  return true;
}

/**
 * Undo = remove *my own* last stroke (never someone else's, and never by absolute index —
 * a concurrent stroke from another member could have been appended after mine since I drew
 * it, so "last in the array" isn't necessarily "my last one").
 */
export function undoLastOwnStroke(array: Y.Array<Stroke>, authorId: string): Stroke | null {
  const items = array.toArray();
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const stroke = items[i];
    if (stroke && stroke.authorId === authorId) {
      array.delete(i, 1);
      return stroke;
    }
  }
  return null;
}

/**
 * Which stroke (if any) a tap/drag point at normalized `(x, y)` hits — topmost first (most
 * recently drawn), since that's what a human expects the eraser to remove.
 */
export function findStrokeAt(strokes: readonly Stroke[], x: number, y: number): Stroke | null {
  for (let i = strokes.length - 1; i >= 0; i -= 1) {
    const stroke = strokes[i];
    if (stroke && hitTestStroke(stroke, x, y)) return stroke;
  }
  return null;
}
